import { useMemo, useState } from "react";
import { useLocale } from "@/hooks/useLocale";
import { fmtNumber } from "@/lib/locale-format";
import { isHebrewText } from "@/lib/rtl-utils";
import { bidiComponents } from "@/lib/bidi-components";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Brain, ChevronDown, ChevronRight, RotateCcw, Zap, ListChecks, MapPin, Target, Search, ClipboardList, Shield, Bot, Coins, ArrowRight, HelpCircle, Swords } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { NarratorMessage, type EntityMedia } from "./NarratorMessage";
import type { DiceRoll } from "@/types/adventure.types";
import { classifyMessage } from "@/lib/message-classification";
import { proseClasses } from "@/lib/prose-classes";
import { MessageCardMenu } from "./MessageCardMenu";
import { MessageDebugDialog } from "./MessageDebugDialog";
import { downloadMessageDebugLog } from "@/lib/message-debug";
import { GMAvatar } from "./GMAvatar";
// ── Soft word limit for game mode ──
const SOFT_WORD_LIMIT = 200;

function truncateToSoftLimit(text: string): { truncated: string; isTruncated: boolean } {
  const words = text.split(/\s+/);
  if (words.length <= SOFT_WORD_LIMIT) return { truncated: text, isTruncated: false };
  const joined = words.slice(0, SOFT_WORD_LIMIT).join(" ");
  const lastSentence = joined.lastIndexOf(".");
  const cutoff = lastSentence > joined.length * 0.6 ? lastSentence + 1 : joined.length;
  return { truncated: text.slice(0, text.indexOf(joined.slice(cutoff - 1)) + 1) || joined, isTruncated: true };
}

export interface MessageCardProps {
  msg: any;
  entityLookup: Map<string, EntityMedia>;
  adventureState: any;
  onSpeak: (msgId: string, text: string, voiceKey: string) => void;
  onSpeakMessage?: (msgId: string, fullContent: string) => void;
  onDownload?: (msgId: string, text: string, voiceKey: string) => void;
  onPause?: () => void;
  onResume?: () => void;
  onStop?: () => void;
  speakingMsgId: string | null;
  speakingVoiceKey: string | null;
  isPaused?: boolean;
  defaultVoiceId: string | null;
  onLocationClick?: (locationName: string) => void;
  onSpeakerClick?: (speakerName: string) => void;
  isHidden?: boolean;
  onToggleHide?: () => void;
  onReclassify?: (msgId: string, newCategory: string) => void;
  onDelete?: (msgId: string) => void;
  onEdit?: (msgId: string, content: string) => void;
  onRetry?: (msgId: string) => void;
  canEdit?: boolean;
  isSceneIntro?: boolean;
  isLastMessage?: boolean;
  isDeactivated?: boolean;
  isAdmin?: boolean;
  /** True when the current user owns the world this adventure belongs to.
   *  Combined with isAdmin to gate the per-message debug tools. */
  isWorldOwner?: boolean;
  /** World id for the parent adventure — used as context for content reports. */
  worldId?: string | null;
  /** Current user id. Used to decide whether to show the "Report" action
   *  (own messages aren't reportable). */
  currentUserId?: string | null;
  /** Author of this message, when known (e.g. multiplayer player_id). Used to
   *  hide the report action on the user's own user-role messages. Optional —
   *  when omitted we fall back to msg.role === "user" gating. */
  authorUserId?: string | null;
}

// ── Agent Debug Dialog (admin only — full page on mobile) ──
function AgentDebugDialog({ metadata }: { metadata: any }) {
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  const classification = metadata?.intent_classification;
  const directorNotes = metadata?.director_notes;
  const subAgentSummary = metadata?.sub_agent_summary;
  const subActions = metadata?.sub_actions;
  const inputTokens = metadata?.input_tokens;
  const outputTokens = metadata?.output_tokens;
  const creditsUsed = metadata?.credits_used;
  const stateChanges = metadata?.state_changes;
  const mechanicResults = metadata?.mechanic_results;
  const toolsCalled = metadata?.tools_called;
  const agentsUsed = metadata?.agents_used;
  const isInspection = classification?.is_inspection;
  const isPlanning = classification?.is_planning;
  const inspectionTarget = classification?.inspection_target;

  const hasContent = classification || directorNotes || subAgentSummary || subActions?.length;
  if (!hasContent) return null;

  // Count total items for the trigger badge
  const agentCount = agentsUsed?.length || (subAgentSummary ? 1 : 0);
  const toolCount = toolsCalled || 0;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5 rounded-full text-muted-foreground/50 hover:text-primary hover:bg-primary/10"
        aria-label="View agent debug information"
        onClick={() => setOpen(true)}
      >
        <Brain className="w-3 h-3" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-auto p-0 gap-0 w-full h-full sm:h-auto sm:w-auto">
          <DialogHeader className="px-4 pt-4 pb-2 border-b border-border/50">
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Brain className="w-4 h-4 text-primary" />
              Agent Process
            </DialogTitle>
            {/* Quick stats row */}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {classification?.primary_intent && (
                <Badge variant="outline" className="text-[10px] h-5 gap-1">
                  <Target className="w-3 h-3" />
                  {classification.primary_intent}
                </Badge>
              )}
              {isInspection && (
                <Badge variant="outline" className="text-[10px] h-5 gap-1 text-amber-500 border-amber-500/30">
                  <Search className="w-3 h-3" />
                  Inspect{inspectionTarget ? `: ${inspectionTarget}` : ""}
                </Badge>
              )}
              {isPlanning && (
                <Badge variant="outline" className="text-[10px] h-5 gap-1 text-blue-500 border-blue-500/30">
                  <ClipboardList className="w-3 h-3" />
                  Planning
                </Badge>
              )}
              {agentCount > 0 && (
                <Badge variant="secondary" className="text-[10px] h-5 gap-1">
                  <Bot className="w-3 h-3" />
                  {agentCount} agents
                </Badge>
              )}
              {toolCount > 0 && (
                <Badge variant="secondary" className="text-[10px] h-5 gap-1">
                  <Zap className="w-3 h-3" />
                  {toolCount} tools
                </Badge>
              )}
            </div>
          </DialogHeader>

          <div className="px-4 py-3 space-y-3 text-xs overflow-auto">
            {/* Classification */}
            {classification && (
              <DebugSection icon={<Target className="w-3 h-3 text-primary" />} title="Classification">
                <div className="flex flex-wrap gap-1 mb-1">
                  <Badge variant="default" className="text-[10px] h-4">{classification.primary_intent}</Badge>
                  {classification.secondary_intents?.map((s: string) => (
                    <Badge key={s} variant="outline" className="text-[10px] h-4 text-muted-foreground">{s}</Badge>
                  ))}
                  {classification.echo_risk && (
                    <Badge variant="outline" className="text-[10px] h-4 text-orange-500 border-orange-500/30">Echo Risk</Badge>
                  )}
                </div>
                {classification.reasoning && (
                  <p className="text-muted-foreground italic leading-snug mt-1">{classification.reasoning}</p>
                )}
              </DebugSection>
            )}

            {/* Sub-Actions */}
            {subActions?.length > 0 && (
              <DebugSection icon={<ListChecks className="w-3 h-3 text-secondary" />} title="Sub-Actions">
                <div className="space-y-0.5">
                  {subActions.map((sa: any, i: number) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <span className="text-primary font-mono shrink-0 text-[10px]">{i + 1}.</span>
                      <span className="text-foreground/80">{typeof sa === "string" ? sa : `[${sa.intent}] ${sa.text}`}</span>
                    </div>
                  ))}
                </div>
              </DebugSection>
            )}

            {/* Director Notes */}
            {directorNotes && (
              <DebugSection icon={<Brain className="w-3 h-3 text-violet-500" />} title="Director">
                {directorNotes.narrative_guidance && (
                  <p className="text-foreground/70 mb-1">📝 {directorNotes.narrative_guidance}</p>
                )}
                {directorNotes.pacing_transition && (
                  <div className="flex items-center gap-1 text-foreground/70 mb-1">
                    <ArrowRight className="w-3 h-3" />
                    <span>Pacing → <span className="font-medium">{directorNotes.pacing_transition.new_phase}</span></span>
                  </div>
                )}
                {directorNotes.advance_turn !== undefined && (
                  <div className="text-foreground/70 mb-1">
                    Turn advance: <span className={cn("font-medium", directorNotes.advance_turn ? "text-green-500" : "text-muted-foreground")}>{directorNotes.advance_turn ? "Yes" : "No"}</span>
                  </div>
                )}
                {directorNotes.inject_encounter && (
                  <div className="text-amber-500/80 mb-1">⚔️ Encounter: {directorNotes.inject_encounter.description}</div>
                )}
                {directorNotes.suggested_mechanics?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-1">
                    <span className="text-muted-foreground">Suggested:</span>
                    {directorNotes.suggested_mechanics.map((m: string) => (
                      <Badge key={m} variant="outline" className="text-[10px] h-4">{m}</Badge>
                    ))}
                  </div>
                )}
                {directorNotes.npc_intents?.length > 0 && (
                  <div className="space-y-0.5 mt-1">
                    {directorNotes.npc_intents.map((npc: any, i: number) => (
                      <div key={i} className="text-foreground/70">
                        <span className="font-medium">{npc.name}:</span> <span className="italic">{npc.intent}</span>
                      </div>
                    ))}
                  </div>
                )}
                {directorNotes.mercy_event && (
                  <div className="text-green-500/80">🩹 Mercy: {directorNotes.mercy_event.description}</div>
                )}
                {directorNotes.escalation_event && (
                  <div className="text-red-500/80">🔥 Escalation: {directorNotes.escalation_event.description}</div>
                )}
              </DebugSection>
            )}

            {/* Validity Check */}
            {metadata?.validity_check && !metadata.validity_check.is_valid && (
              <DebugSection icon={<Shield className="w-3 h-3 text-red-500" />} title="Anti-Cheat">
                <div className="text-red-400">
                  <span className="font-medium">{metadata.validity_check.violation_type}</span>
                  {metadata.validity_check.violation_details && (
                    <p className="text-red-400/70 mt-0.5">{metadata.validity_check.violation_details}</p>
                  )}
                </div>
              </DebugSection>
            )}

            {/* State Changes */}
            {stateChanges && Object.keys(stateChanges).length > 0 && (
              <DebugSection icon={<MapPin className="w-3 h-3 text-green-500" />} title="State Changes">
                <div className="space-y-1">
                  {Object.entries(stateChanges).map(([key, val]) => (
                    <div key={key} className="flex flex-col gap-0.5 w-full max-w-full">
                      <span className="text-muted-foreground font-mono shrink-0">{key}:</span>
                      {typeof val === "string" ? (
                        <span className="text-foreground/80 break-all">{val}</span>
                      ) : (
                        <pre className="text-[10px] text-foreground/80 whitespace-pre-wrap font-mono bg-muted/30 rounded p-1.5 w-full break-words max-h-40 overflow-y-auto">
                          {JSON.stringify(val, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              </DebugSection>
            )}

            {/* Sub-Agent Summary */}
            {subAgentSummary && (
              <DebugSection icon={<Bot className="w-3 h-3 text-secondary" />} title="Sub-Agents">
                <pre className="text-[10px] text-foreground/70 whitespace-pre-wrap font-mono bg-muted/30 rounded p-2 max-h-40 overflow-auto">
                  {typeof subAgentSummary === "string" ? subAgentSummary : JSON.stringify(subAgentSummary, null, 2)}
                </pre>
              </DebugSection>
            )}

            {/* Token Usage */}
            {(inputTokens || outputTokens || creditsUsed) && (
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground border-t border-border pt-2 mt-2">
                {inputTokens && <span>In: {fmtNumber(inputTokens, locale)} tok</span>}
                {outputTokens && <span>Out: {fmtNumber(outputTokens, locale)} tok</span>}
                {creditsUsed != null && (
                  <span className="flex items-center gap-0.5">
                    <Coins className="w-3 h-3" />
                    {creditsUsed < 0.01 ? Number(creditsUsed).toFixed(4) : Number(creditsUsed).toFixed(2)} cr
                  </span>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DebugSection({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{title}</span>
      </div>
      <div className="pl-4">{children}</div>
    </div>
  );
}
export function MessageCard({
  msg, entityLookup, adventureState, onSpeak, onSpeakMessage, onDownload, onPause, onResume, onStop,
  speakingMsgId, speakingVoiceKey, isPaused, defaultVoiceId, onLocationClick, onSpeakerClick,
  isHidden, onToggleHide, onReclassify, onDelete, onEdit, onRetry, canEdit, isSceneIntro, isLastMessage,
  isDeactivated, isAdmin, isWorldOwner, worldId, currentUserId, authorUserId,
}: MessageCardProps) {
  const { t } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");

  const isOOC = (msg.metadata as any)?.is_ooc === true;
  const isInnerVoice = (msg.metadata as any)?.is_inner_voice === true;
  const isGmQuery = (msg.metadata as any)?.is_gm_query === true;
  const isSystemNote = (msg.metadata as any)?.is_system_note === true;
  const msgCategory = classifyMessage(msg);
  const isPlayer = msg.role === "user";
  const isRtl = useMemo(() => isHebrewText(msg.content), [msg.content]);
  const isNarrator = msg.role === "narrator";
  const canTTS = isNarrator && !isOOC;

  // Collapsible for secondary message types
  const isCollapsible = !isPlayer && (msgCategory === "system" || msgCategory === "ooc" || msgCategory === "gm_query" || msgCategory === "introspection");
  const [isOpen, setIsOpen] = useState(!isCollapsible);

  const speakSegment = (text: string, voiceKey: string) => onSpeak(msg.id, text, voiceKey);
  const downloadSegment = onDownload ? (text: string, voiceKey: string) => onDownload(msg.id, text, voiceKey) : undefined;
  const speakFull = () => onSpeakMessage ? onSpeakMessage(msg.id, msg.content) : onSpeak(msg.id, msg.content, "narrator");
  const downloadFull = () => onDownload?.(msg.id, msg.content, "narrator");
  const isThisMsgPlaying = speakingMsgId === msg.id;

  const variables = adventureState?.variables;
  const timeInfo = variables ? {
    hour: variables.game_hour, day: variables.game_day,
    phase: variables.day_phase, weather: variables.current_weather,
  } : undefined;

  const { displayContent, showExpand } = useMemo(() => {
    if (isPlayer || isOOC || isInnerVoice || isGmQuery) return { displayContent: msg.content, showExpand: false };
    if (expanded || isLastMessage) return { displayContent: msg.content, showExpand: !isLastMessage };
    const { truncated, isTruncated } = truncateToSoftLimit(msg.content);
    return { displayContent: isTruncated ? truncated : msg.content, showExpand: isTruncated };
  }, [msg.content, isPlayer, isOOC, isInnerVoice, isGmQuery, expanded, isLastMessage]);

  const handleStartEdit = () => { setEditContent(msg.content); setIsEditing(true); };
  const handleSaveEdit = () => {
    if (editContent.trim() && editContent !== msg.content) onEdit?.(msg.id, editContent.trim());
    setIsEditing(false);
  };
  const handleCancelEdit = () => { setIsEditing(false); setEditContent(""); };

  const creditsUsed = (msg.metadata as any)?.credits_used;

  // Report action: any signed-in user can flag a non-player narrator/system
  // message. For player messages, only allow reporting other players' content.
  const isOwnMessage = !!currentUserId && (
    (authorUserId && authorUserId === currentUserId) ||
    (!authorUserId && isPlayer)
  );
  const canReport = !!currentUserId && !isOwnMessage && !isSystemNote;

  // Admins and world owners can inspect the full AI process for any message.
  const canDebug = !!isAdmin || !!isWorldOwner;
  const [debugOpen, setDebugOpen] = useState(false);

  const menuElement = (
    <>
    <MessageCardMenu
      msgId={msg.id}
      msgContent={msg.content}
      msgCategory={msgCategory}
      canTTS={canTTS}
      isThisMsgPlaying={isThisMsgPlaying}
      isPaused={isPaused}
      onSpeak={speakFull}
      onDownload={downloadFull}
      onPause={onPause}
      onResume={onResume}
      onStop={onStop}
      onToggleHide={onToggleHide}
      isHidden={isHidden}
      onReclassify={onReclassify}
      onDelete={onDelete}
      onEdit={canEdit ? handleStartEdit : undefined}
      canEdit={canEdit}
      onRetry={onRetry ? () => onRetry(msg.id) : undefined}
      creditsUsed={creditsUsed}
      canReport={canReport}
      worldId={worldId ?? null}
      onDebugVisualize={canDebug ? () => setDebugOpen(true) : undefined}
      onDebugDownload={canDebug ? () => downloadMessageDebugLog(msg) : undefined}
    />
    {canDebug && (
      <MessageDebugDialog msg={msg} open={debugOpen} onOpenChange={setDebugOpen} />
    )}
    </>
  );

  const cardBody = (
    <>
      {isEditing ? (
        <div className="space-y-2">
          <textarea
            dir="auto"
            value={editContent}
            onChange={e => setEditContent(e.target.value)}
            className="w-full min-h-[80px] rounded-md border border-border bg-background px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
            autoFocus
          />
          <div className="flex items-center gap-2 justify-end">
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleCancelEdit}>{t.cancel}</Button>
            <Button size="sm" className="h-7 text-xs" onClick={handleSaveEdit}>{t.save}</Button>
          </div>
        </div>
      ) : (
        <>
          {msg.role === "narrator" && !isOOC && !isInnerVoice && !isGmQuery && !isSystemNote ? (
            <NarratorMessage
              content={displayContent}
              entityLookup={entityLookup}
              onSpeakSegment={speakSegment}
              onDownloadSegment={downloadSegment}
              speakingKey={speakingMsgId === msg.id ? speakingVoiceKey : null}
              diceRolls={msg.metadata?.dice_rolls as DiceRoll[] | undefined}
              locationChange={(msg.metadata as any)?.state_changes?.current_location}
              sceneImageUrl={msg.metadata?.scene_image_url as string | undefined}
              timeInfo={timeInfo}
              onLocationClick={onLocationClick}
              onSpeakerClick={onSpeakerClick}
              offscreenActions={(msg.metadata as any)?.offscreen_actions}
            />
          ) : (
            <>
            {isPlayer && (() => {
              const match = displayContent.match(/^\[ACTION:\s*([^\]]+)\]/);
              if (match) {
                const actionLabel = match[1];
                const rest = displayContent.slice(match[0].length).replace(/\s*\{\{ACTION_META:[^}]+\}\}/g, "").trim();
                return (
                  <div className="flex flex-wrap items-center gap-1.5" dir="auto">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-medium text-primary">
                      <Swords className="w-3 h-3" />
                      {actionLabel}
                    </span>
                    {rest && <span className="text-sm text-foreground/80">{rest}</span>}
                  </div>
                );
              }
              return null;
            })()}
            {(!isPlayer || !displayContent.match(/^\[ACTION:/)) && (
              <div className={cn(proseClasses, isOOC && "italic", isInnerVoice && "italic text-violet-300/90")} dir="auto">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={bidiComponents}>{displayContent}</ReactMarkdown>
              </div>
            )}
            </>
          )}
          {showExpand && (
            <button onClick={() => setExpanded(!expanded)} className="mt-2 flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors">
              <ChevronDown className={cn("w-3 h-3 transition-transform", expanded && "rotate-180")} />
              {expanded ? "Show less" : "Read more…"}
            </button>
          )}
          {/* Retry button for narrator messages */}
          {isNarrator && !isOOC && !isInnerVoice && !isGmQuery && onRetry && (
            <div className="mt-2 flex items-center">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[11px] gap-1 text-muted-foreground hover:text-foreground"
                onClick={() => onRetry(msg.id)}
              >
                <RotateCcw className="w-3 h-3" />
                Retry
              </Button>
            </div>
          )}
          {/* Retry button for player messages when it's the last message */}
          {isPlayer && isLastMessage && onRetry && (
            <div className="mt-2 flex items-center">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[11px] gap-1 text-muted-foreground hover:text-foreground"
                onClick={() => onRetry(msg.id)}
              >
                <RotateCcw className="w-3 h-3" />
                Retry
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );

  const menuOverlay = (
    <div className={cn("absolute top-1.5 opacity-0 group-hover/msg:opacity-100 transition-opacity z-10", isRtl ? "left-1.5" : "right-1.5")}>
      {menuElement}
    </div>
  );

  // ── System note chip ──
  if (isSystemNote) {
    const noteText = msg.content.replace(/^\[System correction:\s*/, "").replace(/\]$/, "");
    return (
      <div className="flex items-center gap-2 py-1.5 px-3 mx-auto max-w-2xl">
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
          <Zap className="w-3.5 h-3.5 shrink-0" />
          <span>{noteText}</span>
        </div>
      </div>
    );
  }

  // ── Scene intro ──
  if (isSceneIntro) {
    const introProse = msg.content.replace(/^[\s]*[-*+]\s+/gm, "").replace(/^[\s]*\d+\.\s+/gm, "");
    return (
      <div dir={isRtl ? "rtl" : undefined} className={cn("relative py-6 px-4 sm:px-8 text-center group/msg max-w-full w-full mx-auto break-words overflow-hidden [overflow-wrap:anywhere]", isHidden && "opacity-40")}>
        {menuOverlay}
        <div className="mx-auto max-w-2xl space-y-3">
          <div className="h-px w-24 mx-auto bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
          <div className={cn(proseClasses, "text-base sm:text-lg leading-relaxed italic text-muted-foreground prose-strong:text-foreground")} dir="auto">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={bidiComponents}>{introProse}</ReactMarkdown>
          </div>
          <div className="h-px w-24 mx-auto bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
        </div>
      </div>
    );
  }

  // Header label for narrator messages
  const headerLabel = isInnerVoice ? (
    "Inner Voice"
  ) : isGmQuery ? (
    "Prompt"
  ) : (
    "Narrator"
  );

  // ── Collapsible card (system, ooc, gm_query, introspection) ──
  if (isCollapsible) {
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div
          dir={isRtl ? "rtl" : undefined}
          className={cn(
            "my-5 overflow-hidden group/msg relative max-w-full w-full break-words [overflow-wrap:anywhere] rounded-lg bg-muted/15 border border-border/30",
            isHidden && "opacity-40",
          )}
        >
          {menuOverlay}
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/10 transition-colors">
              <ChevronRight className={cn("w-3 h-3 text-muted-foreground/50 transition-transform shrink-0", isOpen && "rotate-90")} />
              <span className="text-[11px] text-muted-foreground/60 font-medium">{headerLabel}</span>
              {!isOpen && (
                <span className="text-[10px] text-muted-foreground/40 truncate flex-1 ml-1">
                  {msg.content.slice(0, 80)}{msg.content.length > 80 ? "…" : ""}
                </span>
              )}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-3 pb-3 pt-2">
              {cardBody}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    );
  }

  // ── Standard card ──
  return (
    <div
      dir={isRtl ? "rtl" : undefined}
      className={cn(
        "my-5 overflow-hidden group/msg relative break-words [overflow-wrap:anywhere] min-w-0 max-w-full transition-opacity rounded-lg",
        isDeactivated && "opacity-40 pointer-events-none",
        isPlayer
          ? cn("max-w-[85%] sm:max-w-[75%] ml-auto bg-primary/[0.03] border border-primary/10 px-3 py-2.5 sm:px-4 sm:py-3", isRtl && "mr-auto ml-0")
          : cn("max-w-full w-full bg-muted/15 border border-border/30 px-3 py-2.5 sm:px-4 sm:py-3"),
        isHidden && "opacity-40",
      )}
    >
      {menuOverlay}
      {/* Action echo — compact inline */}
      {!isPlayer && (msg.metadata as any)?.action_echo && (
        <span className="inline-flex items-center gap-1 text-[10px] text-primary/60 italic mb-1">
          <Zap className="w-2.5 h-2.5" />
          {(msg.metadata as any).action_echo}
        </span>
      )}
      {cardBody}
    </div>
  );
}
